import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { fetchCustomerDetail } from '@thunks/customerDetail';
import { selectMobilePlanData } from '@selectors/mobilePlan';

import { getRouteUrl } from '@utils/urls';
import { getSession } from '@utils/jwt';

import { HOME, MOBILE_ORDER_FORM } from '@common/routes';
import Layout from '@common/containers/Layout';
import * as MultiStep from '@common/contexts/multiStepContext';

import FormWrapper from './FormWrapper';
import { MOBILE_ORDER_FORM_CURRENT_STEP } from './MobileOrderForm.helpers';

export const MobileOrderForm = ({ providersList, mobileTermsLinks }) => {
  const { t } = useTranslation();
  const mobilePlan = useSelector(selectMobilePlanData);
  const dispatch = useDispatch();
  const {
    locale,
    query: { plan },
  } = useRouter();

  const getToken = useCallback(async () => {
    const session = await getSession();

    return session?.accessToken;
  }, []);

  useEffect(() => {
    if (!getToken()) return;

    dispatch(fetchCustomerDetail());
  }, [dispatch, getToken]);

  return (
    <Layout
      description={t(
        'mobile.orderForm.meta.description',
        '{{planName}} Abo bestellen',
        {
          planName: mobilePlan?.title,
        },
      )}
      title={t('mobile.orderForm.meta.title', 'Abo bestsellen')}
      canonicalPageUrl={getRouteUrl(locale, MOBILE_ORDER_FORM, {
        plan: mobilePlan?.id,
      })}
    >
      <MultiStep.Wrapper
        localStorageKey={`${MOBILE_ORDER_FORM_CURRENT_STEP}-PLAN-${plan}`}
      >
        <MultiStep.RouteLayout
          backRoute={HOME}
          backRouteAs={getRouteUrl(locale, HOME)}
          columns={8}
          stickyTitle={t('mobile.orderForm.title', 'Abo bestsellen')}
        >
          <FormWrapper
            providersList={providersList}
            mobileTermsLinks={mobileTermsLinks}
          />
        </MultiStep.RouteLayout>
      </MultiStep.Wrapper>
    </Layout>
  );
};

MobileOrderForm.propTypes = {
  providersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      logo: PropTypes.string,
    }),
  ).isRequired,
  mobileTermsLinks: PropTypes.shape({
    dataProtectionDeclaration: PropTypes.string,
    termsAndConditions: PropTypes.string,
  }).isRequired,
};

export default MobileOrderForm;
